import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_QrCodeGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/qr-code-generator',
			name: 'QR Code Generator — Create Scannable Codes Instantly with Lift',
			description:
				"Generate a QR code from a link, text, email, SMS, phone number, or Wi-Fi network in seconds. Lift's QR code generator supports six content types. Try Lift now.",
			url: 'https://liftapp.ai/qr-code-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/qr-code-generator#product' },
				{ '@id': 'https://liftapp.ai/qr-code-generator#howto' },
				{ '@id': 'https://liftapp.ai/qr-code-generator#benefits' },
				{ '@id': 'https://liftapp.ai/qr-code-generator#faq' },
				{ '@id': 'https://liftapp.ai/qr-code-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/qr-code-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/qr-code-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/qr-code-generator#product',
			name: 'QR Code Generator',
			description:
				'Turn links, text, contact details, or Wi-Fi credentials into a scannable QR code with Lift. Pick a content type, fill in the fields, and download a ready-to-print PNG in seconds.',
			url: 'https://liftapp.ai/qr-code-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/qr-code-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Choose your QR type',
					text: 'Select Link, Text, Email, SMS, Phone, or Wi-Fi and keep entries saved across tabs.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Add your content details',
					text: 'Fill in the required fields for that type, like a link, message, or Wi-Fi credentials.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Generate your QR code',
					text: 'Lift creates a scannable QR code once every required field is complete.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download or regenerate anytime',
					text: 'Save the PNG or tap Regenerate to create a new code instantly.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/qr-code-generator#benefits',
			name: 'Who can benefit from the Lift QR Code Generator?',
			description:
				"Lift's QR Code Generator is useful for small business owners, event organizers, hosts, job seekers, and content creators who want scannable codes for links, contact details, or Wi-Fi.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Small business owners — Turn a link to your menu, store, or product page into a printable code. Generate it from the Link tab and download a ready-to-print PNG in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Event organizers — Send guests straight to an RSVP inbox without printing an email address. Enter the address and message on the Email tab and add the code to invitations or posters.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Hosts & landlords — Let guests join your Wi-Fi without reading out a password. Fill in the network name and password on the Wi-Fi tab, then print the code near the router.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Job seekers — Let recruiters call you straight from a resume or business card without saving a number. Enter your number on the Phone tab and place the generated code on printed materials.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Content creators — Give fans a one-tap way to text you for exclusive drops or updates. Fill in your number and message on the SMS tab, then post the code on stories or packaging.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'QR Code Generator by Lift',
				description:
					'Example of how Lift QR Code Generator turns links, contact details, and Wi-Fi credentials into scannable codes.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/qr-code-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift QR Code Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift QR Code Generator turns links, text, contact details, or Wi-Fi credentials into a scannable QR code. It supports six content types: Link, Text, Email, SMS, Phone, and Wi-Fi.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift QR Code Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Choose a content type, fill in the required fields, and tap Generate to create your QR code. Once it's ready, download the PNG or tap Regenerate to make a new version.",
					},
				},
				{
					'@type': 'Question',
					name: 'What types of QR codes can I create with Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift supports six QR code types: Link, Text, Email, SMS, Phone number, and Wi-Fi network. Each type has its own fields, so the code always matches the content you enter.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I edit or regenerate my QR code after creating it?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Switch back to any tab, update the details, and tap Regenerate to create a new code instantly. Your previous entries stay saved so you can adjust and try again.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to enter my Wi-Fi password or personal details in Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes the information you enter, including Wi-Fi credentials, securely and uses it only to generate your QR code.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/qr-code-generator#why-lift',
			name: "Why Lift's QR Code Generator stands out",
			description:
				"Lift's QR Code Generator combines high-quality results, intelligent rendering, cross-platform access, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift QR Code Generator',
				description:
					"Comparison of Lift's QR Code Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/qr-code-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/qr-code-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'QR Code Generator',
					item: 'https://liftapp.ai/qr-code-generator',
				},
			],
		},
	],
};
